import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Grid } from '@mui/material';
import PropTypes from 'prop-types';

const statesList = [
  { name: 'Alabama', code: 'AL' },
  { name: 'Alaska', code: 'AK' },
  { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' },
  { name: 'California', code: 'CA' },
  { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' },
  { name: 'Delaware', code: 'DE' },
  { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' },
  { name: 'Hawaii', code: 'HI' },
  { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' },
  { name: 'Indiana', code: 'IN' },
  { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' },
  { name: 'Kentucky', code: 'KY' },
  { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' },
  { name: 'Maryland', code: 'MD' },
  { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' },
  { name: 'Minnesota', code: 'MN' },
  { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' },
  { name: 'Montana', code: 'MT' },
  { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' },
  { name: 'New Hampshire', code: 'NH' },
  { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' },
  { name: 'New York', code: 'NY' },
  { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' },
  { name: 'Ohio', code: 'OH' },
  { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' },
  { name: 'Pennsylvania', code: 'PA' },
  { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' },
  { name: 'South Dakota', code: 'SD' },
  { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' },
  { name: 'Utah', code: 'UT' },
  { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' },
  { name: 'Washington', code: 'WA' },
  { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' },
  { name: 'Wyoming', code: 'WY' },
];

function AddressFields({
  align,
  isZip,
  isAddressOne,
  isAddressTwo,
  isCity,
  isState,
  zip,
  addressOne,
  addressTwo,
  city,
  state,
  handleChange,
  type,
  required,
  isZipRequired,
}) {
  const [selectedState, setSelectedState] = useState(null);

  useEffect(() => {
    const selectedOption = statesList.find((option) => `${option.code}` === state);
    setSelectedState(selectedOption || null);
  }, [state]);

  const numberOfInputs = [isAddressOne, isAddressTwo, isCity, isState, isZip].filter((x) => x === true).length ?? 5;
  const inputSize = align?.match(/row/) ? 12 / numberOfInputs : 12;

  const handleStateChange = (event, newValue) => {
    handleChange({
      target: {
        id: 'state',
        value: `${newValue?.code ?? ''}`,
      },
    });
    setSelectedState(newValue);
  };

  const handleChangeValidateFirst = (event, type) => {
    const specialCharRegex = /[^a-zA-Z0-9\s]/;
    if (!specialCharRegex.test(event.target.value)) {
      handleChange(event, type);
    }
  };

  return (
    <Grid container flexDirection={align} rowSpacing={2} columnSpacing={2}>
      {isAddressOne && (
        <Grid item xs={12} sm={12} md={inputSize}>
          <TextField
            id="addressOne"
            type="text"
            label="Address One"
            inputProps={{ 'data-cy': 'address-one-field' }}
            value={addressOne ?? ''}
            fullWidth
            onChange={(e) => handleChange(e, type)}
            variant="outlined"
            required={required}
          />
        </Grid>
      )}
      {isAddressTwo && (
        <Grid item xs={12} sm={12} md={inputSize}>
          <TextField
            id="addressTwo"
            type="text"
            label="Address Two"
            inputProps={{ 'data-cy': 'address-two-field' }}
            value={addressTwo ?? ''}
            fullWidth
            onChange={(e) => handleChange(e, type)}
            variant="outlined"
          />
        </Grid>
      )}
      {isCity && (
        <Grid item xs={12} sm={12} md={inputSize}>
          <TextField
            id="city"
            type="text"
            label="City"
            inputProps={{ 'data-cy': 'city-field' }}
            value={city ?? ''}
            fullWidth
            onChange={(e) => handleChangeValidateFirst(e, type)}
            variant="outlined"
            required={required}
          />
        </Grid>
      )}
      {isState && (
        <Grid item xs={12} sm={12} md={inputSize}>
          <Autocomplete
            id="state"
            data-cy="state-select"
            value={selectedState}
            onChange={handleStateChange}
            options={statesList}
            getOptionLabel={(option) => `${option.name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select State"
                InputLabelProps={{
                  id: 'state-select-label',
                }}
                fullWidth
                id="state"
                variant="outlined"
                required={required}
              />
            )}
          />
        </Grid>
      )}
      {isZip && (
        <Grid item xs={12} sm={12} md={inputSize}>
          <TextField
            id="zip"
            type="text"
            label="Zip"
            inputProps={{ 'data-cy': 'zip-field' }}
            value={zip ?? ''}
            fullWidth
            onChange={(e) => handleChange(e, type)}
            variant="outlined"
            required={isZipRequired}
          />
        </Grid>
      )}
    </Grid>
  );
}

AddressFields.propTypes = {
  zip: PropTypes.any,
  addressOne: PropTypes.any,
  addressTwo: PropTypes.any,
  city: PropTypes.any,
  state: PropTypes.any,
  isZip: PropTypes.any,
  isAddressOne: PropTypes.any,
  isAddressTwo: PropTypes.any,
  isCity: PropTypes.any,
  isState: PropTypes.any,
  handleChange: PropTypes.any,
  type: PropTypes.any,
  required: PropTypes.bool,
  isZipRequired: PropTypes.bool,
  align: PropTypes.any,
};

export default AddressFields;
